import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { setFollow } from "../../api/companies";
import type { CompanyDetail, HistoryEntry } from "../../api/companies";
import { ApiError, errorText } from "../../api/client";
import { AdviceCard } from "../../components/AdviceCard";
import { Button, buttonClass } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { StatusChip } from "../../components/StatusChip";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { AdviceActions } from "../claims/ClaimActions";
import { formatAgo, formatDate } from "../../lib/format";
import styles from "./companies.module.css";
import { COMPANIES_KEY, useCompanyDetail } from "./queries";

const t = nl.companies;

function adviceTitle(company: CompanyDetail): string {
  const { kind, days_left: days } = company.advice;
  if (kind === "own_claim" && days !== null) return t.ownClaimDays(days);
  return t.adviceTitles[kind];
}

function FollowButton({ company }: { company: CompanyDetail }) {
  const client = useQueryClient();
  const toast = useToast();
  const follow = useMutation({
    mutationFn: (next: boolean) => setFollow(company.id, next),
    onSuccess: (_, next) => {
      void client.invalidateQueries({ queryKey: COMPANIES_KEY });
      toast({
        message: next ? t.followed(company.name) : t.unfollowed(company.name),
        onUndo: () => {
          follow.mutate(!next);
        },
      });
    },
    onError: (error) => {
      toast({ message: errorText(error) });
    },
  });
  return (
    <Button
      aria-pressed={company.followed}
      disabled={follow.isPending}
      onClick={() => {
        follow.mutate(!company.followed);
      }}
    >
      {company.followed ? t.following : t.follow}
    </Button>
  );
}

function History({ entries }: { entries: HistoryEntry[] }) {
  if (entries.length === 0) return <p className={styles.muted}>{t.historyEmpty}</p>;
  const current = entries.filter((e) => !e.old);
  const old = entries.filter((e) => e.old);
  const years = [...new Set(old.map((e) => e.academic_year))];
  const item = (entry: HistoryEntry) => (
    <li key={entry.id} className={styles.historyItem}>
      <span className={styles.historyOutcome}>
        {t.outcomes[entry.outcome]}
        {entry.reason && ` · ${t.reasons[entry.reason]}`}
        {entry.channel && ` · ${t.channels[entry.channel]}`}
      </span>
      <span className={styles.muted}>
        {formatDate(entry.created_at)} · {entry.by ? t.by(entry.by) : t.anonymous}
      </span>
    </li>
  );
  return (
    <>
      {current.length > 0 && <ul className={styles.history}>{current.map(item)}</ul>}
      {years.map((year) => (
        <div key={year} className={styles.oldYear}>
          <h4 className={styles.oldYearTitle}>{t.previousYear(year)}</h4>
          <ul className={styles.history}>{old.filter((e) => e.academic_year === year).map(item)}</ul>
        </div>
      ))}
    </>
  );
}

export function CompanyDetailView({ company, inSheet = false }: { company: CompanyDetail; inSheet?: boolean }) {
  const note =
    company.advice.disabled_reason !== null
      ? t.disabled[company.advice.disabled_reason]
      : company.waitlist_position !== null
        ? t.waitlistPosition(company.waitlist_position)
        : company.no_response_count >= 2
          ? t.noResponse(company.no_response_count)
          : undefined;
  return (
    <article className={styles.detail} aria-labelledby="company-title">
      <header className={styles.detailHeader}>
        <StatusChip status={company.status} />
        {/* In the sheet the name is already its title. */}
        <h2 id="company-title" className={inSheet ? "sr-only" : styles.detailTitle}>
          {company.name}
        </h2>
        <p className={styles.muted}>
          {[
            company.municipality,
            company.is_sole_proprietorship ? t.soleProprietorship : null,
            company.kbo_number ? t.kbo(company.kbo_number) : null,
            company.last_update_at ? formatAgo(company.last_update_at) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <div className={styles.headerActions}>
          <FollowButton company={company} />
        </div>
      </header>

      <AdviceCard
        status={company.status}
        title={adviceTitle(company)}
        text={t.adviceTexts[company.advice.kind]}
        actions={<AdviceActions company={company} />}
        note={note}
      />

      <section aria-labelledby="facts-title">
        <h3 id="facts-title" className={styles.sectionTitle}>
          {t.facts}
        </h3>
        <dl className={styles.facts}>
          <div>
            <dt>{t.spotsFree}</dt>
            <dd>{t.spotsValue(company.spots_free, company.spots_total)}</dd>
          </div>
          <div>
            <dt>{t.reportsThisYear}</dt>
            <dd>{company.reports_this_year}</dd>
          </div>
          <div>
            <dt>{t.itManagement}</dt>
            <dd>{t.it[company.it_management]}</dd>
          </div>
        </dl>
      </section>

      {company.msp && (
        <Link className={styles.followLine} to={`/bedrijven/${company.msp.id}`}>
          <span className={styles.followLineTitle}>{t.followTheLine(company.msp.name)}</span>
          <span className={styles.muted}>{t.followTheLineText}</span>
        </Link>
      )}

      <section aria-labelledby="history-title">
        <h3 id="history-title" className={styles.sectionTitle}>
          {t.history}
        </h3>
        <History entries={company.history} />
      </section>

      {company.tips.length > 0 && (
        <section aria-labelledby="tips-title">
          <h3 id="tips-title" className={styles.sectionTitle}>
            {t.tips}
          </h3>
          <ul className={styles.tips}>
            {company.tips.map((tip) => (
              <li key={`${tip.created_at}-${tip.text.slice(0, 12)}`} className={styles.tip}>
                <p>{tip.text}</p>
                <span className={styles.muted}>
                  {formatDate(tip.created_at)} · {tip.by ? t.by(tip.by) : t.anonymous}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function DetailSkeleton() {
  return (
    <div className={styles.detail} role="status" aria-busy="true" aria-label={nl.common.loading}>
      <Skeleton width={90} height={24} radius={999} />
      <Skeleton width="70%" height={28} />
      <Skeleton width="40%" />
      <Skeleton height={140} radius={16} />
      <Skeleton height={80} radius={12} />
    </div>
  );
}

/** Loads one company; shows a skeleton, a not-found or an error state with retry. */
export function CompanyDetailPanel({ id, inSheet = false }: { id: string; inSheet?: boolean }) {
  const detail = useCompanyDetail(id);
  if (detail.isPending) return <DetailSkeleton />;
  if (detail.isError) {
    const missing = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className={styles.detail}>
        <EmptyState
          title={missing ? t.notFound : t.detailError}
          action={
            missing ? (
              <Link className={buttonClass()} to="/">
                {t.back}
              </Link>
            ) : (
              <Button onClick={() => void detail.refetch()}>{nl.common.retry}</Button>
            )
          }
        />
      </div>
    );
  }
  return <CompanyDetailView company={detail.data} inSheet={inSheet} />;
}
